/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { useDropzone } from 'react-dropzone';
import { DndProvider } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { getColor } from '@panel/commons/core/themev3';
import { select } from '@panel/commons/core/themev2';
import Icon from '../../../commons/UploadIcon.js';
import {
  backendForDND,
  MAX_FILES,
  createPicture,
  dropZoneConfig,
  keysImagesErrors,
  validateImageErrors,
  resolveImageDimensions,
  isValidImageFormat,
  normalizeFileWithMimeType
} from './helpers';
import { ItemList } from './itemlist';
import { ModuleWrapper } from '../moduleWrapper';
import {
  SubtitleHelper,
  SectionContainer,
  FilesContainer,
  DropzoneContainer,
  CollapseContainer,
  CollapseWrapper
} from './styles.js';
import {
  getPictures,
  addMultimedia,
  updateMultimedia,
  deleteMultimedia,
  moveMultimedia,
  MultimediaType,
  MultimediaCollection,
  addErrorMultimedia
} from '../../../../ducks/multimedia';
import ToggleButton from '../../../commons/ToggleButton';
import useSectionsCollapse from '../../../../hooks/useSectionsCollapse.js';

const ImageSection = ({ device, postingId, showErrorSnackbar, validating, setValidating }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { images } = useSelector(getPictures);
  const isDisabled = images.length >= MAX_FILES;
  const hasLowLimit = images.length >= 0 && images.length < 5 && validating;
  const { registerRef, getMaxHeight, isCollapsed, setCollapsed } = useSectionsCollapse({
    visibleItems: 5,
    itemSelector: '.image-item',
    dependencies: [images.length]
  });

  const fileDispatcher = (files = []) => {
    // Calculamos cuántos archivos podemos aceptar, restando los que ya tiene cargado el usuario
    const availableFiles = MAX_FILES - images.length;

    // Si no hay espacio disponible, mostramos mensaje
    if (availableFiles <= 0) {
      showErrorSnackbar('ImageLimitMax', t('professionalPubli.multimedia.image.errorLimitMax'));
      return; // Salimos de la función para evitar añadir más archivos
    }
    // Mostramos mensaje si se rechazaron archivos por límite excedido
    if (files.length > availableFiles) {
      showErrorSnackbar('LimitMaxError', t('professionalPubli.multimedia.image.errorLimitMax'));
    }
    // Mostramos mensaje si algún archivo tiene errores
    setTimeout(() => {
      if (files.some(file => file.error)) {
        showErrorSnackbar('ValidationError', t('professionalPubli.multimedia.image.hasError'));
      }
      // Pequeño retraso para asegurar que se procese el estado correctamente y se muestre el segundo snackbar
    }, 100);

    // Tomamos solo la cantidad de archivos que podemos aceptar
    const filesToProcess = files.slice(0, availableFiles);
    // Añadimos los archivos aceptados
    filesToProcess.forEach(file => {
      const picture = createPicture(file, MultimediaType.IMAGE);
      dispatch(addMultimedia(picture, MultimediaCollection.IMAGE));
    });
  };

  const onDrop = async acceptedFiles => {
    if (acceptedFiles.length === 0) return;
    // Reiniciamos la validación de imagenes.
    setValidating(false);
    // Validamos y obtenenos dimensiones de cada archivo
    const processedFiles = await Promise.all(
      acceptedFiles.map(async file => {
        // Normalizamos primero el type del file:
        const normalizedFile = normalizeFileWithMimeType(file);
        // Verificamos luego el formato del file:
        if (!isValidImageFormat(normalizedFile)) {
          return { ...normalizedFile, error: keysImagesErrors.format };
        }
        // Obtenemos las dimensiones
        const result = await resolveImageDimensions(normalizedFile);
        // Validamos las restricciones de dimensiones y peso:
        return validateImageErrors(result.file, result.dimensions, keysImagesErrors);
      })
    );
    // Enviamos los archivos procesados al dispatcher
    fileDispatcher(processedFiles);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...dropZoneConfig,
    onDrop
  });

  const onDelete = id => {
    dispatch(deleteMultimedia(id, MultimediaCollection.IMAGE));
  };

  const onUpdate = multimedia => {
    dispatch(updateMultimedia(multimedia, MultimediaCollection.IMAGE));
  };

  const onMove = (dragIndex, hoverIndex) => {
    dispatch(moveMultimedia(dragIndex, hoverIndex, MultimediaCollection.IMAGE));
  };

  const onError = (id, keyError = 'errorGeneric') => {
    dispatch(addErrorMultimedia(id, MultimediaCollection.IMAGE, `professionalPubli.multimedia.image.${keyError}`));
    showErrorSnackbar('ValidationError', t('professionalPubli.multimedia.image.hasError'));
  };

  const getColorIcon = () => {
    if (isDisabled) {
      return select('midNeutralGray');
    }
    if (hasLowLimit) {
      return select('redNoTRecommended');
    }
    return getColor('primary');
  };

  return (
    <ModuleWrapper title={t('professionalPubli.multimedia.image.title')}>
      <SubtitleHelper data-publi='multimedia-images-subtitle'>
        {t('professionalPubli.multimedia.image.subtitle')}
      </SubtitleHelper>
      <SectionContainer>
        <DndProvider backend={backendForDND}>
          <CollapseWrapper data-publi='multimedia-images-collapseWrapper'>
            <CollapseContainer
              data-publi='multimedia-images-collapseContainer'
              device={device}
              ref={registerRef}
              maxHeight={getMaxHeight()}
              hasToggleButton={images.length > 5}
              hasItems={images.length > 0}
            >
              <FilesContainer
                data-publi='multimedia-images-filesContainer'
                hasItems={images.length > 0}
                device={device}
              >
                <DropzoneContainer
                  data-publi='multimedia-images-dropzone'
                  {...getRootProps()}
                  disabled={isDisabled}
                  hasItems={images.length > 0}
                  device={device}
                  hasLowLimit={hasLowLimit}
                >
                  <input data-publi='multimedia-images-input' {...getInputProps()} disabled={isDisabled} />
                  {!isDragActive && (
                    <>
                      <Icon width={32} height={32} fill={getColorIcon()} viewBox='0 0 32 32' />
                      <span data-publi='multimedia-images-spanText'>{t('professionalPubli.multimedia.image.add')}</span>
                    </>
                  )}
                </DropzoneContainer>
                <ItemList
                  data-publi='multimedia-images-itemlist'
                  items={images}
                  onUpdate={onUpdate}
                  onDelete={onDelete}
                  onMove={onMove}
                  onError={onError}
                  mainMarker
                  mainMarkerTitle={t('professionalPubli.multimedia.image.mainMarker')}
                  hasMain
                  onMainImageChange={onMove}
                  photoType='picture'
                  postingId={postingId}
                  itemSelector='image-item'
                  device={device}
                />
              </FilesContainer>
            </CollapseContainer>
            {images.length > 5 && (
              <ToggleButton
                dataPubli='multimedia-images-showMore'
                device={device}
                title={t(`professionalPubli.multimedia.image.${isCollapsed() ? 'showMore' : 'showLess'}`)}
                active={!isCollapsed()}
                setActive={() => setCollapsed()}
                margin='0 13px 0 0'
                widthIcon='10px'
                heightIcon='6.1px'
              />
            )}
          </CollapseWrapper>
        </DndProvider>
      </SectionContainer>
    </ModuleWrapper>
  );
};

export { ImageSection };
